import { useDisplayLocale } from "@/shared/composables/useDisplayLocale";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { useWorkspaceCurrency } from "@/shared/composables/useWorkspaceCurrency";
/** What an amount reads while the workspace currency is loading or unknown. */
export const UNKNOWN_AMOUNT = "—";
/**
 * Formats project amounts (objective, collected, allocations) in the workspace reference
 * currency: OPROJ stores no currency per project, and the donations it sums are created in the
 * workspace currency.
 *
 * Without a currency an amount reads {@link UNKNOWN_AMOUNT} rather than a bare number: a figure
 * with no unit next to amounts that carry one would read as being in the same currency.
 */
export function useProjectAmounts() {
    const { currency } = useWorkspaceCurrency();
    const { formatAmount } = useFinanceFormatters();
    const displayLocale = useDisplayLocale();
    function formatProjectAmount(value) {
        return currency.value ? formatAmount(value, currency.value) : UNKNOWN_AMOUNT;
    }
    /**
     * An exported file must not lose the figures: without a currency every row carries the bare
     * amount (to the cent, display locale) instead of the on-screen dash.
     */
    function formatProjectAmountForExport(value) {
        return currency.value
            ? formatAmount(value, currency.value)
            : value.toLocaleString(displayLocale.value, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
    }
    return { formatProjectAmount, formatProjectAmountForExport };
}
